import React from 'react';

import { useNavigate } from 'react-router-dom';

import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Avatar, Box, Card, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { CollectiveMember } from '../types';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { useToast } from 'hooks/useToast';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import Scheduling from 'pages/AgendaConfiguration/components/Scheduling';
import { createEdge, getEdges, removeEdge } from 'pages/IndividualBio/services/edgeService';

type Props = {
  member: CollectiveMember;
  loggedUserId: string;
  onConnectionAction?: () => void;
};

function getCommonConnectionsText(names: string[]) {
  if (names.length === 0) return 'Nenhuma conexão em comum';
  if (names.length === 1)
    return (
      <>
        {names[0]} <span style={{ fontWeight: 700 }}>é conexão em comum</span>
      </>
    );
  if (names.length === 2)
    return (
      <>
        {names[0]}, {names[1]} <span style={{ fontWeight: 700 }}>são conexões em comum</span>
      </>
    );
  return (
    <>
      {names[0]}, {names[1]}{' '}
      <span style={{ fontWeight: 700 }}>e mais {names.length - 2} conexões em comum</span>
    </>
  );
}

const MemberCard: React.FC<Props> = ({ member, loggedUserId, onConnectionAction }) => {
  const { toastSuccess, toastError } = useToast();
  const actions = member.connection_data?.actions || [];
  const isFavorited = actions.includes('FAVORITED') || actions.includes('LIKE');
  const [loadingFavorite, setLoadingFavorite] = React.useState(false);
  const [localFavorited, setLocalFavorited] = React.useState(isFavorited);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const navigate = useNavigate();
  const { selectMenuByDocumentId } = useMenuSelection();
  const menuRoutes = useMenuRoutes();
  const { user } = useUser();
  const [openScheduling, setOpenScheduling] = React.useState(false);

  const [likeCount, setLikeCount] = React.useState(member.connection_data?.like_count ?? 0);
  const [dislikeCount, setDislikeCount] = React.useState(
    member.connection_data?.dislike_count ?? 0
  );
  const [whatsappCount, setWhatsappCount] = React.useState(
    member.connection_data?.whatsapp_count ?? 0
  );
  const [loadingLike, setLoadingLike] = React.useState(false);
  const [loadingDislike, setLoadingDislike] = React.useState(false);
  const [loadingWhatsapp, setLoadingWhatsapp] = React.useState(false);
  const [likeStatus, setLikeStatus] = React.useState(actions.includes('LIKE'));
  const [dislikeStatus, setDislikeStatus] = React.useState(actions.includes('DISLIKE'));

  const theme = useTheme();

  React.useEffect(() => {
    setLocalFavorited(isFavorited);
    setLikeStatus(actions.includes('LIKE'));
    setDislikeStatus(actions.includes('DISLIKE'));
    setLikeCount(member.connection_data?.like_count ?? 0);
    setDislikeCount(member.connection_data?.dislike_count ?? 0);
    setWhatsappCount(member.connection_data?.whatsapp_count ?? 0);
  }, [isFavorited, actions, member.connection_data]);

  const handleToggleFavorite = async () => {
    setLoadingFavorite(true);
    try {
      if (localFavorited) {
        const edges = await getEdges(loggedUserId, member.uuid);
        const favoriteEdge = edges.find(
          (e: any) => e.type_code === 'FAVORITED' || e.type_code === 'LIKE'
        );
        if (favoriteEdge) {
          await removeEdge(favoriteEdge.id);
          setLocalFavorited(false);
        }
      } else {
        await createEdge(loggedUserId, member.uuid, 'FAVORITED');
        setLocalFavorited(true);
      }
    } finally {
      setLoadingFavorite(false);
    }
  };

  const handleLike = async () => {
    setLoadingLike(true);
    try {
      if (likeStatus) {
        // Remove LIKE
        const edges = await getEdges(loggedUserId, member.uuid);
        const likeEdge = edges.find((e: any) => e.type_code === 'LIKE');
        if (likeEdge) {
          await removeEdge(likeEdge.id);
          setLikeStatus(false);
          setLikeCount(c => Math.max(0, c - 1));
        }
      } else {
        // Remove DISLIKE if exists
        if (dislikeStatus) {
          const edges = await getEdges(loggedUserId, member.uuid);
          const dislikeEdge = edges.find((e: any) => e.type_code === 'DISLIKE');
          if (dislikeEdge) {
            await removeEdge(dislikeEdge.id);
            setDislikeStatus(false);
            setDislikeCount(c => Math.max(0, c - 1));
          }
        }
        await createEdge(loggedUserId, member.uuid, 'LIKE');
        setLikeStatus(true);
        setLikeCount(c => c + 1);
      }
    } finally {
      setLoadingLike(false);
    }
  };

  const handleDislike = async () => {
    setLoadingDislike(true);
    try {
      if (dislikeStatus) {
        // Remove DISLIKE
        const edges = await getEdges(loggedUserId, member.uuid);
        const dislikeEdge = edges.find((e: any) => e.type_code === 'DISLIKE');
        if (dislikeEdge) {
          await removeEdge(dislikeEdge.id);
          setDislikeStatus(false);
          setDislikeCount(c => Math.max(0, c - 1));
        }
      } else {
        // Remove LIKE if exists
        if (likeStatus) {
          const edges = await getEdges(loggedUserId, member.uuid);
          const likeEdge = edges.find((e: any) => e.type_code === 'LIKE');
          if (likeEdge) {
            await removeEdge(likeEdge.id);
            setLikeStatus(false);
            setLikeCount(c => Math.max(0, c - 1));
          }
        }
        await createEdge(loggedUserId, member.uuid, 'DISLIKE');
        setDislikeStatus(true);
        setDislikeCount(c => c + 1);
      }
    } finally {
      setLoadingDislike(false);
    }
  };

  const handleWhatsapp = async () => {
    setLoadingWhatsapp(true);
    try {
      await createEdge(loggedUserId, member.uuid, 'WHATSAPP');
      setWhatsappCount(c => c + 1);
    } finally {
      setLoadingWhatsapp(false);
    }
  };

  const handleViewBio = () => {
    const route = menuRoutes.find(r => r.documentId === 'c7jp5768k53sra3u6bv5ynxg');
    if (route) {
      const menuContext = {
        documentId: route.documentId,
        path: route.path,
        preserveLevel: true,
      };

      selectMenuByDocumentId(route.documentId, true, undefined, route.path, true);
      navigate(route.path, {
        state: {
          selectedUser: member.uuid,
          menuContext,
        },
      });
    } else {
      console.warn('Rota não encontrada para o documentId');
    }
  };

  const commonNodes = member.connection_data?.common_nodes || [];
  const names = commonNodes.map(n => n.name);
  const showExtra = names.length > 2;
  const extraCount = names.length - 2;
  const tooltipText = names.length ? names.join(', ') : 'Nenhuma conexão em comum';
  const isRequest = member.connection_data?.relationship_status === 'PENDING';
  const isRequestInitiator = member.connection_data?.relationship_initiator === loggedUserId;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAcceptConnection = async () => {
    try {
      await createEdge(loggedUserId, member.uuid, 'CONNECTION');
      toastSuccess('Conexão aceita com sucesso');
      onConnectionAction?.();
    } catch (error) {
      toastError('Erro ao aceitar conexão');
    }
  };

  const handleRejectConnection = async () => {
    try {
      await createEdge(loggedUserId, member.uuid, 'REJECT_CONNECTION');
      toastSuccess('Conexão rejeitada com sucesso');
      onConnectionAction?.();
    } catch (error) {
      toastError('Erro ao rejeitar conexão');
    }
  };

  const handleRemoveConnection = async () => {
    try {
      await createEdge(loggedUserId, member.uuid, 'REJECT_CONNECTION');
      toastSuccess('Conexão removida com sucesso');
      onConnectionAction?.();
    } catch (error) {
      toastError('Erro ao remover conexão');
    }
  };

  const handleRequestConnection = async () => {
    try {
      await createEdge(loggedUserId, member.uuid, 'CONNECTION');
      toastSuccess('Conexão solicitada com sucesso');
      onConnectionAction?.();
    } catch (error) {
      toastError('Erro ao solicitar conexão');
    }
  };

  const handleRemoveRequestConnection = async () => {
    try {
      await createEdge(loggedUserId, member.uuid, 'REJECT_CONNECTION');
      toastSuccess('Solicitação removida com sucesso');
      onConnectionAction?.();
    } catch (error) {
      toastError('Erro ao remover conexão');
    }
  };

  const actionsMenu = [
    {
      label: 'Ver Bio Individual',
      onClick: handleViewBio,
      show: true,
    },
    {
      label: 'Aceitar Solicitação Recebida',
      onClick: handleAcceptConnection,
      show: isRequest && !isRequestInitiator,
    },
    {
      label: 'Rejeitar Solicitação Recebida',
      onClick: handleRejectConnection,
      show: isRequest && !isRequestInitiator,
    },
    {
      label: `Remover Conexão`,
      onClick: handleRemoveConnection,
      show: member.connection_data?.relationship_status === 'ACCEPTED',
    },
    {
      label: `Solicitar Conexão`,
      onClick: handleRequestConnection,
      show:
        member.connection_data?.relationship_status !== 'ACCEPTED' &&
        member.connection_data?.relationship_status !== 'PENDING',
    },
    {
      label: `Remover Solicação Enviada`,
      onClick: handleRemoveRequestConnection,
      show: isRequest && isRequestInitiator,
    },
    {
      label: 'Agendar',
      onClick: () => setOpenScheduling(true),
      show: member.connection_data?.level && member.connection_data?.level > 0,
    },
    // {
    //   label: `Gostar (${likeCount})`,
    //   onClick: handleLike,
    //   show: true,
    //   loading: loadingLike,
    // },
    // {
    //   label: `Não Gostar (${dislikeCount})`,
    //   onClick: handleDislike,
    //   show: true,
    //   loading: loadingDislike,
    // },
    /* {
      label: `Compartilhar Whatsapp (${whatsappCount})`,
      onClick: handleWhatsapp,
      show: true,
      loading: loadingWhatsapp,
    }, */
  ];

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        border: isRequest && !isRequestInitiator ? `1px solid ${theme.palette.primary.main}` : '',
        borderRadius: 2,
      }}
    >
      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
        <IconButton
          size="small"
          color="error"
          onClick={handleToggleFavorite}
          disabled={loadingFavorite}
          sx={{ marginLeft: -1, marginTop: -1 }}
        >
          {localFavorited ? (
            <FavoriteIcon sx={{ color: '#D70708' }} />
          ) : (
            <FavoriteBorderIcon sx={{ color: '#D70708' }} />
          )}
        </IconButton>
      </Box>
      <Stack direction="column" gap={1}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="start" gap={1}>
            <Avatar src={member.photo_url} sx={{ width: 48, height: 48 }} />
            <Stack direction="column">
              <Typography variant="h6" fontWeight="bold">
                {member.first_name} {member.last_name}{' '}
                {/* {`${member.connection_data?.level && member.connection_data?.level > 0 ? ` - ${member.connection_data?.level}°` : ''}`} */}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {member.company_data?.name}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {member.company_data?.role}
              </Typography>
            </Stack>
          </Stack>
          <Box>
            <IconButton onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Stack>
        <Stack direction="column">
          <Tooltip title={names.length > 2 ? tooltipText : ''} arrow placement="left">
            <Typography color="text.secondary" variant="body2">
              {getCommonConnectionsText(names)}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            background: 'background.primary',
            color: 'text.primary',
            minWidth: 220,
          },
        }}
      >
        {actionsMenu
          .filter(a => a.show)
          .map((action, idx) => (
            <MenuItem
              key={idx}
              onClick={() => {
                action.onClick();
                handleMenuClose();
              }}
            >
              <ListItemText primary={action.label} />
            </MenuItem>
          ))}
      </Menu>
      <GlobalModal
        open={openScheduling}
        title="Confirmação"
        onClose={() => setOpenScheduling(false)}
        width="32rem"
        height="auto"
        keepMounted
      >
        <Scheduling
          requesterId={user?.id ?? ''}
          hostId={member.uuid}
          onClose={() => setOpenScheduling(false)}
          hostName={`${member.first_name} ${member.last_name}`}
        />
      </GlobalModal>
    </Card>
  );
};

export default MemberCard;
